/* eslint-disable complexity */
import { AbsoluteNumberInput, FloatNumberInput } from '@/components';
import { TableCell, TableRow } from '@/components/ui/table';
import { AbsatzVorgabe } from '@/domain/controlling/entities';
import { useTranslation } from 'react-i18next';
import { AbsoluteNumberField, ZielstundenField } from '../../../components';
import { PrognoseUebernehmenButton } from './PrognoseUebernehmenButton';
import { useTagebuchEinstellungen } from '@/domain/controlling/hooks/useTagebuchEinstellungen';
import { useDialogContext } from '../DialogContext';
import { calculatAbsatzZielminuten } from './calculateZielminuten';
import { maskitoParseNumber } from '@maskito/kit';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useRouteParams } from '@/domain/controlling/pages/Umsatzplanung/Planung/Dialog/useRouteParams';
import { useMindestbelegung } from '@/domain/controlling/hooks/useMindestbelegung/useMindestbelegung';

// eslint-disable-next-line max-lines-per-function
export function AbsatzPlanWerteTableRow() {
  const { t } = useTranslation();
  const { dispatch, planwerteForm } = useDialogContext();
  const {
    currentStandort: { id: standortId },
  } = useStandortContext();
  const { tagebuchEinstellungen } = useTagebuchEinstellungen(standortId);
  const { absatzVorgabe } = tagebuchEinstellungen.absatzProduktivitaet;

  const { searchParams } = useRouteParams();
  const { datum } = searchParams;
  const mindestbelegung = useMindestbelegung(datum, standortId);

  function handleUpdateAbsatzPlanwert(value: string) {
    const updatedZielminuten = calculatAbsatzZielminuten(
      maskitoParseNumber(value, ','),
      maskitoParseNumber(planwerteForm.formValues.absatzProduktivitaet, ','),
      absatzVorgabe,
    );
    dispatch({
      type: 'updateAbsatzPlan',
      values: {
        absatzPlan: value,
        absatzZielminuten: updatedZielminuten,
      },
    });
  }

  function handleUpdateAbsatzProduktivitaet(value: string) {
    const updatedZielminuten = calculatAbsatzZielminuten(
      maskitoParseNumber(planwerteForm.formValues.absatzPlan, ','),
      maskitoParseNumber(value, ','),
      absatzVorgabe,
    );
    dispatch({
      type: 'updateAbsatzProduktivitaet',
      values: {
        absatzProduktivitaet: value,
        absatzZielminuten: updatedZielminuten,
      },
    });
  }

  const isZielstundenZero =
    planwerteForm.formValues.absatzPlan.length > 0 &&
    planwerteForm.formValues.absatzProduktivitaet.length > 0 &&
    planwerteForm.formValues.absatzZielminuten === 0;

  const zielstundenUnterMindestbelegung =
    planwerteForm.formValues.absatzZielminuten &&
    planwerteForm.formValues.absatzZielminuten < mindestbelegung;

  return (
    <TableRow className="hover:bg-transparent">
      <TableCell className="font-medium py-4 ps-0 w-1/4">
        <AbsoluteNumberInput
          value={planwerteForm.formValues.absatzPlan}
          onInput={(e) => {
            handleUpdateAbsatzPlanwert(e.currentTarget.value);
          }}
          unit={t('domain.controlling.umsatzplanung.units.stueck')}
        />
      </TableCell>
      <TableCell className="font-medium p-4 w-1/4">
        <div className="flex items-center -ms-3 gap-1">
          <PrognoseUebernehmenButton
            type="absatz"
            absatzPrognose={planwerteForm.formValues.absatzPrognose}
          />
          <AbsoluteNumberField
            value={planwerteForm.formValues.absatzPrognose}
            unit={t('domain.controlling.umsatzplanung.units.stueck')}
          />
        </div>
      </TableCell>
      <TableCell className="font-medium p-4 w-1/4">
        {absatzVorgabe === AbsatzVorgabe.Stueck ? (
          <AbsoluteNumberInput
            value={planwerteForm.formValues.absatzProduktivitaet}
            onInput={(e) => {
              handleUpdateAbsatzProduktivitaet(e.currentTarget.value);
            }}
            unit={t('domain.controlling.umsatzplanung.ziele.Stk./Std.')}
          />
        ) : (
          <FloatNumberInput
            value={planwerteForm.formValues.absatzProduktivitaet}
            onInput={(e) => {
              handleUpdateAbsatzProduktivitaet(e.currentTarget.value);
            }}
            unit={t('domain.controlling.umsatzplanung.ziele.mpt')}
            tooltipMessage={t(
              'domain.controlling.umsatzplanung.planung.dialog.infoMptInputField',
            )}
          />
        )}
      </TableCell>
      <TableCell className="font-medium p-4 w-1/4">
        <div className="flex items-center gap-1">
          <ZielstundenField
            className={`${
              isZielstundenZero || zielstundenUnterMindestbelegung
                ? '[&>div]:text-primary-orange-O500'
                : ''
            }`}
            value={planwerteForm.formValues.absatzZielminuten}
          />
          {zielstundenUnterMindestbelegung && (
            <FontAwesomeIcon
              className="text-primary-orange-O500"
              icon={faTriangleExclamation}
            />
          )}
          {isZielstundenZero && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <FontAwesomeIcon
                    className="text-primary-orange-O500"
                    icon={faTriangleExclamation}
                  />
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[200px]">
                  {absatzVorgabe === AbsatzVorgabe.Mpt
                    ? t(
                        'domain.controlling.umsatzplanung.planung.dialog.absatzKeineZielstundenMpt',
                      )
                    : t(
                        'domain.controlling.umsatzplanung.planung.dialog.absatzKeineZielstundenStueck',
                      )}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}
